import React, { useState } from 'react';
import Footer from './components/Footer';
import SocialLinks from './components/SocialLinks';
import './styles/Home.css';

function Home() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bandcampLoaded, setBandcampLoaded] = useState(false);
  const [activeVideos, setActiveVideos] = useState({});
  const [emailHovered, setEmailHovered] = useState(false);

  const tracklist = [
    "Chant De Mars",
    "If You See This It’s a Sign",
    "Dream Cake",
    "North of the North Pole",
    "Bear Awareness",
    "WasToBe",
    "Debris",
    "Anything But You",
    "Nose Prose",
    "Inner Gimbal"
  ];

  const videos = [
    { id: '4mNHg927uxM', title: 'WasToBe', category: 'Live from Hotel2Tango' },
    { id: '7aJsGdVV3ZM', title: 'Chant De Mars', category: 'Live from Avaloch Farm' },
    { id: 'fTUigUvYNpw', title: 'Nose Prose', category: 'Live from Hotel2Tango' }
  ];

  const handlePlayVideo = (videoId) => {
    setActiveVideos(prev => ({ ...prev, [videoId]: true }));
  };

  const handleEmailInteraction = () => {
    if (!emailHovered) {
      setEmailHovered(true);
    }
  };

  return (
    <div className="home-wrapper">
      {/* Background Glows & Texture */}
      <div className="bg-glow-container">
        <div className="glow-spot glow-1"></div>
        <div className="glow-spot glow-2"></div>
        <div className="glow-spot glow-3"></div>
      </div>
      <div className="bg-texture-overlay"></div>

      {/* Sticky Header */}
      <header className="stick-header">
        <a href="#hero" className="nav-brand">THE PLASTIC WASTE BAND</a>
        <nav className={`nav-group ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <a className="nav-item" href="#bio" onClick={() => setMobileMenuOpen(false)}>Bio</a>
          <a className="nav-item" href="#music" onClick={() => setMobileMenuOpen(false)}>Music</a>
          <a className="nav-item" href="#video" onClick={() => setMobileMenuOpen(false)}>Videos</a>
          <a className="nav-item" href="#contact" onClick={() => setMobileMenuOpen(false)}>Contact</a>
        </nav>
        <button 
          className="mobile-toggler" 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle navigation menu"
        >
          ☰
        </button>
      </header>

      {/* Main Container */}
      <main className="home">
        
        {/* Hero Section / Trash Island Album */}
        <section id="hero" className="hero-container">
          <div className="hero-art-wrapper">
            <div className="hero-art-glow"></div>
            <img 
              src="/images/trash_island_cover.webp" 
              alt="Trash Island Album Art" 
              className="hero-art"
              loading="eager"
            />
          </div>
          <div className="hero-content glass-panel">
            <span className="hero-subtitle">NEW ALBUM OUT NOW</span>
            <h1 className="hero-title">TRASH ISLAND</h1>
            <p className="hero-desc">
              Trash Island envisions a future where all of humanity has washed up on a floating island of plastic waste—the last inhabitable place on Earth. Born from the Montreal jazz community, the band moves through ecstatic, serene, and quietly absurd vignettes of post-apocalyptic life. A sonic release from doomsday anxieties blending psychedelic rock and collective improvised jazz.
            </p>
            
            <div className="tracklist-preview">
              <span style={{ fontFamily: 'var(--font-headings)', fontSize: '1.1rem', marginBottom: '8px', color: 'white' }}>TRACKLIST</span>
              {tracklist.slice(0, 5).map((track, i) => (
                <div key={i} className="track-preview-item">
                  <span><span className="track-number">{String(i+1).padStart(2, '0')}</span> {track}</span>
                  {track === 'WasToBe' && <span style={{ color: 'var(--accent-coral)', fontSize: '0.8rem', fontWeight: 'bold' }}>FEATURED SINGLE</span>}
                </div>
              ))}
              <a href="#music" style={{ color: 'var(--accent-coral)', fontSize: '0.9rem', textDecoration: 'none', marginTop: '5px', alignSelf: 'flex-start', fontWeight: '500' }}>
                View Full Tracklist & Listen &rarr;
              </a>
            </div>

            <div className="hero-ctas">
              <a 
                href="https://plasticwaste.bandcamp.com/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-primary"
              >
                BUY CD / LISTEN NOW
              </a>
              <a href="#music" className="btn-secondary">LISTEN ALBUM</a>
            </div>
          </div>
        </section>

        {/* Bio Section */}
        <section id="bio" className="bio-grid">
          <div className="bio-image-wrapper">
            <div className="bio-image-glow"></div>
            <img 
              src="/images/pwb_rodolfo_large.webp" 
              alt="The Plastic Waste Band group" 
              className="bio-image"
              loading="lazy"
            />
          </div>
          <div className="bio-text-panel glass-panel">
            <h2>Bio</h2>
            <p className="bio-text" style={{ color: 'var(--text-white)' }}>
              The Plastic Waste Band is an experimental rock band born from the jazz community of Montreal, Canada. This is a band in the truest sense: longtime collaborators who fly into psychedelic group improvisations on the wings of finely tuned compositions. Electric guitars, soaring saxophone, thumping acoustic bass, and big drums evoke Led Zeppelin crashing a gig with the John Coltrane Quartet.
            </p>
            <p className="bio-text">
              Following the 2017 release of their self-titled debut album, they chose to donate all proceeds from the album’s sales to charities dedicated to environmental conservation. Their second album, “Crushed,” was released on Montreal label Florafone Records in September 2019, exploring the challenge of creating art in the face of humanity’s destruction. "Revelations EP" (2020) and the new album "Trash Island" (2026) expand on these themes.
            </p>
            <p className="bio-text">
              Since their first show as a band in 2016, The Plastic Waste Band has performed extensively, most notably at the Off Festival de Jazz de Montréal (2018, 2023), TU Jazz Festival (2019, 2022) and the Festival International de Jazz de Montréal (2023).
            </p>
          </div>
        </section>

        {/* Music Section */}
        <section id="music" className="music-container">
          <h2>Listen to the Album</h2>
          
          {!bandcampLoaded ? (
            <div 
              className="spotify-placeholder-card glass-panel"
              onClick={() => setBandcampLoaded(true)}
              style={{ border: '1px solid rgba(99, 154, 191, 0.25)', background: 'linear-gradient(135deg, rgba(99, 154, 191, 0.08) 0%, rgba(15, 17, 30, 0.5) 100%)' }}
            >
              <svg className="spotify-icon" style={{ fill: '#639abf' }} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                <path d="M256,8C119,8,8,119,8,256S119,504,256,504,504,393,504,256,393,8,256,8Zm48.2,326.1h-181L207.9,178h181Z" />
              </svg>
              <div>
                <h3 style={{ color: 'white', marginBottom: '8px' }}>Click to Load Bandcamp Player</h3>
                <p>Stream the full album on Bandcamp</p>
              </div>
              <button className="btn-primary" style={{ background: '#639abf', boxShadow: '0 4px 20px rgba(99, 154, 191, 0.3)' }}>LOAD PLAYER</button>
            </div>
          ) : (
            <iframe
              title="Bandcamp Album Player"
              style={{ border: '0', width: '100%', maxWidth: '700px', height: '472px', borderRadius: '12px', boxShadow: 'var(--shadow-glass)' }}
              src="https://bandcamp.com/EmbeddedPlayer/album=1351742167/size=large/bgcol=080913/linkcol=639abf/artwork=small/transparent=true/"
              seamless
            ></iframe>
          )}
        </section>

        {/* Video Grid Section */}
        <section id="video">
          <h2>Videos</h2>
          <div className="video-grid">
            {videos.map((video) => (
              <div key={video.id} className="video-card glass-panel">
                <div className="video-thumbnail-container">
                  {!activeVideos[video.id] ? (
                    <div onClick={() => handlePlayVideo(video.id)} style={{ width: '100%', height: '100%' }}>
                      <img 
                        src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`} 
                        alt={video.title} 
                        className="video-thumbnail"
                        loading="lazy"
                      />
                      <div className="play-button-overlay">
                        <svg viewBox="0 0 24 24">
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                    </div>
                  ) : (
                    <iframe
                      className="youtube-iframe"
                      src={`https://www.youtube.com/embed/${video.id}?autoplay=1`}
                      title={video.title}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  )}
                </div>
                <div className="video-info">
                  <span className="video-category">{video.category}</span>
                  <h3 className="video-title">{video.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact">
          <div className="contact-card glass-panel">
            <h2>Contact</h2>
            <p>For booking, press, or sync licensing inquiries, please reach out to us at:</p>
            <a 
              href={emailHovered ? "mailto:theplasticwasteband@gmail.com" : "#"} 
              onMouseEnter={handleEmailInteraction}
              onTouchStart={handleEmailInteraction}
              onClick={handleEmailInteraction}
              className="contact-email"
            >
              <span>theplasticwasteband</span>
              <span style={{ display: 'none' }}>scrambler-bot-blocker</span>
              <span>@</span>
              <span>gmail.com</span>
            </a>
            <SocialLinks />
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
}

export default Home;
